import { MFA_FACTOR } from "../consts";
import type { CorporateInfo } from "../types";

export type MfaFactor = typeof MFA_FACTOR[keyof typeof MFA_FACTOR];

export type MfaPreference = "EMAIL" | "TOTP" | "NONE";

/** Stato MFA dell'utente autenticato (GET /usermgmt/user/mfa-user-settings). */
export type MfaUserSettings = {
  enabledFactors: MfaFactor[]
  preferredFactor?: MfaFactor | null
}

/** Metodi MFA abilitati dall'azienda: dipendono solo dai flag email_enabled e totp_enabled. */
export function getCorporateMfaFactors(config?: CorporateInfo["corporate_mfa_config"] | null): MfaFactor[] {
  const factors: MfaFactor[] = [];
  if (config?.email_enabled) factors.push(MFA_FACTOR.EMAIL_OTP);
  if (config?.totp_enabled) factors.push(MFA_FACTOR.SOFTWARE_TOKEN_MFA);
  return factors;
}

/** Converte il preferito BE nel fattore Cognito; NONE, valori assenti o non validi non selezionano un metodo. */
export function getPreferredMfaFactor(preference: unknown): MfaFactor | null {
  switch (preference) {
  case "EMAIL":
    return MFA_FACTOR.EMAIL_OTP;
  case "TOTP":
    return MFA_FACTOR.SOFTWARE_TOKEN_MFA;
  default:
    return null;
  }
}

export function isMfaFactor(value: unknown): value is MfaFactor {
  return Object.values(MFA_FACTOR).includes(value as MfaFactor);
}

/** Converte il fattore Cognito nel valore preferredMfa atteso dal BE. */
export function getMfaPreference(factor?: MfaFactor | null): MfaPreference {
  switch (factor) {
  case MFA_FACTOR.EMAIL_OTP:
    return "EMAIL";
  case MFA_FACTOR.SOFTWARE_TOKEN_MFA:
    return "TOTP";
  default:
    return "NONE";
  }
}

/**
 * Normalizza la risposta GET mfa-user-settings: accetta camelCase o snake_case e il preferito
 * come fattore Cognito o come EMAIL/TOTP. Il fattore preferito è considerato sempre attivo.
 */
export function normalizeMfaUserSettings(data: unknown): MfaUserSettings {
  const raw = (data ?? {}) as Record<string, unknown>;
  const rawFactors = raw.enabledFactors ?? raw.enabled_factors;
  const rawPreferred = raw.preferredFactor ?? raw.preferred_factor;
  const enabledFactors = Array.isArray(rawFactors) ? rawFactors.filter(isMfaFactor) : [];
  const preferredFactor = isMfaFactor(rawPreferred) ? rawPreferred : getPreferredMfaFactor(rawPreferred);

  if (preferredFactor && !enabledFactors.includes(preferredFactor)) {
    enabledFactors.push(preferredFactor);
  }

  return { enabledFactors, preferredFactor };
}

/** Cognito risponde NotAuthorizedException con questo messaggio quando la sessione della challenge non è più valida. */
export function isCognitoInvalidSessionError(error: unknown): boolean {
  return error instanceof Error && error.message.includes("Invalid session for the user");
}

/** VerifySoftwareToken con AccessToken scaduto, revocato o non valido. */
export function isCognitoInvalidAccessTokenError(error: unknown): boolean {
  return error instanceof Error && /access token/i.test(error.message);
}
